import React, { useState } from "react"
import { IconPencil, IconPlus } from "@tabler/icons-react"
import type { Profile } from "@love-irish/types"

import { GrammarTerm } from "@love-irish/grammar-reference"
import { UserPopover, ProfileBadges } from "@love-irish/user-popover"
import ConjugationTable, { ConjugationChart } from "./conjugation_table"
import { PartOfSpeech } from "./part_of_speech"
import HintTooltip from "./hint_tooltip"
import { renderTemplate, firstFormReference } from "./template"
import {
  AddExampleModal,
  AddExampleDraft,
  EditableExample,
} from "./add_example_modal"
import { buildExampleTokens } from "./example_tokens"

export interface CodexExampleView {
  /** The example's global id, for edit/delete. */
  id?: string
  text: string
  full?: string | null
  translation?: string | null
  audioUrl?: string | null
  /** Who contributed this example — the standard generated Profile (not a bespoke shape), so `slug`
   *  (plain) and `username` ("@slug") don't get confused. The hover card lazily fetches the rest by slug. */
  contributor?: Profile | null
  /** Whether the current viewer owns this example (shows edit/delete). */
  editable?: boolean
}

/** Optional example actions. Provided by the connected page; the affordances only render when wired. */
export interface CodexExampleActions {
  /** Show the per-section "Add new example" CTA (the show page; hidden on the condensed list). */
  allowAddExample?: boolean
  /** Create a new example under a sense. Absent = the editor is preview-only. */
  onExampleSubmit?: (
    senseId: string,
    draft: AddExampleDraft
  ) => void | Promise<void>
  /** Save edits to an existing example (edit mode). */
  onExampleUpdate?: (
    example: EditableExample,
    draft: AddExampleDraft
  ) => void | Promise<void>
  /** Delete an example (from the edit modal). */
  onExampleDelete?: (example: EditableExample) => void | Promise<void>
}

export interface CodexSenseView {
  /** The sense's global id, so a new example can be attached to it. */
  id?: string
  definition?: string | null
  translation?: string | null
  register?: string | null
  /** Proper-noun kind of this sense (name | place | brand | organisation | event), or null. */
  entityType?: string | null
  /** Display label for the entity badge, e.g. "Name", "Place". */
  entityLabel?: string | null
  /** Semantic domain (WordNet supersense) + its display label, e.g. "animal" / "Animal". */
  domain?: string | null
  domainLabel?: string | null
  /** Rhetorical genre + its display label, e.g. "proverb" / "Proverb". */
  genre?: string | null
  genreLabel?: string | null
  examples: CodexExampleView[]
}

export interface CodexGrammarRow {
  key?: string
  label: string
  short?: string | null
  description?: string | null
  example?: string | null
  value: string
  valueShort?: string | null
}

export interface CodexSectionView {
  homographIndex?: number | null
  partOfSpeech: string
  partOfSpeechShort?: string | null
  grammar: CodexGrammarRow[]
  senses: CodexSenseView[]
  conjugation?: ConjugationChart | null
  /** Verb metadata the client generates the chart from (when `conjugation` isn't pre-built). No class =>
   *  no chart: a verb without a stated conjugation class shows no table. */
  conjugationClass?: string | null
  conjugationForms?: Record<string, unknown> | null
  /** Provenance of the stored forms, e.g. "BuNaMo" — shown as an attribution under the chart. */
  conjugationSource?: string | null
}

// The label for the form an example demonstrates, derived from its first {{ }} reference using the
// section's own grammar/conjugation labels: "{{ genitive_singular }}" -> "Genitive singular",
// "{{ past.he }}" -> "Past · he". Nil when it only uses the base word.
const deriveFormTag = (
  text: string,
  section: CodexSectionView
): string | null => {
  const reference = firstFormReference(text)
  if (!reference) return null

  if (reference.includes(".")) {
    const [tense, person] = reference.split(".")
    const tenseLabel =
      section.conjugation?.tenses.find((t) => t.tense === tense)?.label ??
      tense.charAt(0).toUpperCase() + tense.slice(1)
    const personLabel =
      section.conjugation?.tenses[0]?.forms.find((f) => f.person === person)
        ?.label ?? person
    return `${tenseLabel} · ${personLabel}`
  }

  return (
    section.grammar.find((row) => row.key === reference)?.label ?? reference
  )
}

/**
 * Renders a headword's nested senses, Teanglann-style: one block per part of speech, each with its
 * grammar rows and a numbered list of senses. Example sentences (with their own audio) show under the
 * sense they belong to.
 */
const CodexSenses: React.FC<
  {
    sections?: CodexSectionView[]
    headword?: string
    /** Hide the conjugation table (for the condensed list view). The conjugation data is still used to
     *  resolve example templates like {{ past.he }}. */
    showConjugation?: boolean
    item?: unknown
  } & CodexExampleActions
> = ({
  sections,
  headword,
  showConjugation = true,
  item,
  allowAddExample,
  onExampleSubmit,
  onExampleUpdate,
  onExampleDelete,
}) => {
  // One modal for both create (no example) and edit (an example), scoped to the chosen section.
  const [exampleModal, setExampleModal] = useState<{
    section: CodexSectionView
    senseId?: string
    example?: EditableExample
  } | null>(null)

  if (!sections || sections.length === 0) return null

  return (
    <div className="mt-6 space-y-8" data-testid="codex-senses">
      {sections.map((section, sectionIndex) => {
        // Teanglann puts gender right after the part of speech; the rest of the grammar
        // (genitive, plural, verbal noun, ...) goes in a compact parenthetical.
        const gender = section.grammar.find((row) => row.key === "gender")
        const inflections = section.grammar.filter(
          (row) => row.key !== "gender"
        )
        // What an example can reference, from the SAME builder the editor modal uses so the main view
        // and the preview stay in sync: the base word, grammar fields ({{ genitive_singular }}),
        // conjugation cells ({{ past.he }}), AND the tense-base tokens ({{ present }}, {{ past }}).
        // The latter only existed in the modal before, so authored {{ present }} rendered there but not
        // here — building both from buildExampleTokens fixes that.
        const exampleContext = buildExampleTokens([section], headword ?? "").context

        return (
          <section key={section.partOfSpeech} className="group">
            {/* Header line: "rith¹ n. m. (gs. reatha, pl. rití)"; superscript only for homographs. */}
            <div className="">
              <p className="text-sm mb-1 flex flex-wrap items-center gap-1 ">
                {headword && section.homographIndex && (
                  <span className="font-semibold mr-1">
                    {headword}
                    <sup>{section.homographIndex}</sup>
                  </span>
                )}
                <PartOfSpeech type={section.partOfSpeech} item={item} />
                {gender && (
                  <>
                    {" "}
                    <GrammarTerm
                      termKey={gender.key}
                      label={gender.label}
                      description={gender.description}
                      example={gender.example}
                    >
                      <HintTooltip content={gender.value}>
                        <abbr className="no-underline italic">
                          {gender.valueShort || gender.value}
                        </abbr>
                      </HintTooltip>
                    </GrammarTerm>
                  </>
                )}
                {inflections.length > 0 && (
                  <span className="text-muted-foreground">
                    {" ("}
                    {inflections.map((row, index) => (
                      <span key={row.key || row.label}>
                        {index > 0 && ", "}
                        <GrammarTerm
                          termKey={row.key}
                          label={row.label}
                          description={row.description}
                          example={row.example}
                        >
                          <HintTooltip content={row.label}>
                            <abbr className="no-underline">
                              {row.short || row.label}
                            </abbr>
                          </HintTooltip>
                        </GrammarTerm>{" "}
                        <span className="text-foreground">
                          {row.valueShort || row.value}
                        </span>
                      </span>
                    ))}
                    {")"}
                  </span>
                )}
              </p>

              <ol className="space-y-1 text-sm">
                {section.senses.map((sense, senseIndex) => (
                  <li key={senseIndex}>
                    <span className="font-semibold mr-1">
                      {senseIndex + 1}.
                    </span>
                    <span>{sense.translation || sense.definition}</span>
                    {sense.entityLabel && (
                      // Plain text, not a badge: the highlighted dynamic form already carries the
                      // visual treatment, so a competing pill would be noise. Distinguishes each
                      // referent under one "Proper noun" heading (Name vs Place for a shared spelling).
                      <span
                        className="ml-2 text-xs italic text-muted-foreground"
                        data-testid="entity-label"
                      >
                        {sense.entityLabel}
                      </span>
                    )}
                    {sense.register && (
                      <span className="ml-2 text-xs uppercase text-muted-foreground">
                        {sense.register}
                      </span>
                    )}
                    {sense.genreLabel && (
                      <span
                        className="ml-2 text-xs italic text-muted-foreground"
                        data-testid="genre-label"
                      >
                        {sense.genreLabel}
                      </span>
                    )}
                    {sense.domainLabel && (
                      <span
                        className="ml-2 text-xs italic text-muted-foreground"
                        data-testid="domain-label"
                      >
                        {sense.domainLabel}
                      </span>
                    )}

                    {sense.examples.length > 0 && (
                      <ul className="ml-5 mt-2 space-y-1.5 text-muted-foreground">
                        {sense.examples.map((example, exampleIndex) => (
                          <li
                            key={exampleIndex}
                            className="flex items-center gap-2"
                          >
                            {example.audioUrl && (
                              <audio
                                controls
                                src={example.audioUrl}
                                className="h-6"
                                aria-label={`Play ${
                                  example.full || example.text
                                }`}
                              />
                            )}
                            <span>
                              <span className="text-foreground">
                                {renderTemplate(
                                  example.text,
                                  exampleContext,
                                  {
                                    swungDash: true,
                                    // Proper-noun forms capitalize automatically (Saoirse, Dublin).
                                    capitalizeValues: Boolean(sense.entityType),
                                  }
                                )}
                              </span>
                              {example.translation && (
                                <em> {example.translation}</em>
                              )}
                              {(() => {
                                const tag = deriveFormTag(example.text, section)
                                return (
                                  tag && (
                                    <span className="ml-2 rounded bg-earth-50 px-1.5 py-0.5 text-xs text-earth-800">
                                      {tag}
                                    </span>
                                  )
                                )
                              })()}

                              {/* Attribution: who added this example, and edit/delete for the owner. */}
                              {example.contributor && (
                                <span className="ml-2 whitespace-nowrap text-xs text-muted-foreground">
                                  (
                                  <UserPopover
                                    username={example.contributor.slug}
                                    level={example.contributor.currentLevel}
                                  >
                                    {/* username is already the "@slug" form — don't prepend another @. */}
                                    {example.contributor.username}
                                  </UserPopover>
                                  )
                                  <ProfileBadges
                                    profile={{
                                      level: example.contributor.currentLevel,
                                      isNpc: example.contributor.type === "Profile::Npc",
                                    }}
                                    className="ml-1"
                                  />
                                  {example.editable && allowAddExample && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExampleModal({
                                          section,
                                          example: {
                                            id: example.id,
                                            text: example.full ?? example.text,
                                            translation:
                                              example.translation ?? "",
                                          },
                                        })
                                      }
                                      aria-label="Edit example"
                                      className="ml-1 px-0.5 hover:text-foreground"
                                    >
                                      <IconPencil
                                        size={12}
                                        className="inline"
                                      />
                                    </button>
                                  )}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Per-sense "Add new example" CTA, indented to align with the example list. */}
                    {allowAddExample && (
                      <button
                        type="button"
                        onClick={() =>
                          setExampleModal({ section, senseId: sense.id })
                        }
                        className="ml-5 mt-3 inline-flex items-center gap-1 text-xs font-medium transition-colors text-muted-foreground/50 hover:text-primary-600 group-hover:text-muted-foreground"
                      >
                        <IconPlus size={14} /> Add new example
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            </div>

            {showConjugation && section.partOfSpeech === "verb" && (
              <ConjugationTable conjugation={section.conjugation} />
            )}
          </section>
        )
      })}

      {/* One modal for create + edit, scoped to the chosen section's dynamic strings. */}
      {exampleModal && (
        <AddExampleModal
          open
          onOpenChange={(next) => !next && setExampleModal(null)}
          headword={headword ?? ""}
          example={exampleModal.example}
          onSubmit={
            onExampleSubmit && exampleModal.senseId
              ? (draft) =>
                  onExampleSubmit(exampleModal.senseId as string, draft)
              : undefined
          }
          onUpdate={onExampleUpdate}
          onDelete={onExampleDelete}
          {...buildExampleTokens([exampleModal.section], headword ?? "")}
        />
      )}
    </div>
  )
}

export default CodexSenses
